import { useState, useMemo, useEffect } from 'react';
import { useParticipantEPR } from '../ParticipantEPRContext';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Skeleton
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

const ACCENT = 'rgb(219, 234, 254)';    // #DBEAFE
const ACCENT_HOVER = '#EFF6FF';         // blue-50-ish
const ACCENT_SELECTED = '#BFDBFE';      // blue-200
const ACCENT_BORDER = '#60A5FA';        // blue-400

export default function Notes() {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const { eprData, loading, reload } = useParticipantEPR();
  const documents = eprData?.eprDocs || [];

  // Most recent first (desc)
  const sortedDocs = useMemo(() => {
    return [...documents].sort((a, b) => {
      const ta = dayjs(a.date).valueOf() || 0;
      const tb = dayjs(b.date).valueOf() || 0;
      return tb - ta;
    });
  }, [documents]);

  // Auto-select latest when list loads
  useEffect(() => {
    if (!selectedDoc && sortedDocs.length) setSelectedDoc(sortedDocs[0]);
  }, [sortedDocs, selectedDoc]);

  // Keep selection stable across reloads
  useEffect(() => {
    if (!selectedDoc) return;
    const stillThere = sortedDocs.find(d => d.id === selectedDoc.id);
    if (!stillThere && sortedDocs.length) setSelectedDoc(sortedDocs[0]);
  }, [sortedDocs, selectedDoc]);

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '98%',  }}>
      {/* Sidebar */}
      <Paper
        elevation={0}
        sx={{
          width: 260,
          minWidth: 220,
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          bgcolor: 'white',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            px: 2, py: 1.5,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            Patient documents
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
          <Chip
            label={`${sortedDocs.length} item${sortedDocs.length === 1 ? '' : 's'}`}
            size="small"
            sx={{ bgcolor: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
          />
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ p: 2 }}>
              {[...Array(4)].map((_, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton variant="rounded" height={20} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && (
            <List dense disablePadding>
              {sortedDocs.map((doc) => {
                const selected = selectedDoc?.id === doc.id;
                return (
                  <ListItemButton
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    selected={selected}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selected ? ACCENT_BORDER : 'transparent',
                      bgcolor: selected ? ACCENT_SELECTED : 'transparent',
                      '&:hover': { bgcolor: ACCENT_HOVER }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: ACCENT_BORDER }}>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {doc.title}
                        </Typography>
                      }
                      secondary={
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {dayjs(doc.timestamp).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Paper>

      {/* Reader pane */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          bgcolor: 'white',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            px: 3, py: 2,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height:'48px'
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
            {selectedDoc ? selectedDoc.title : 'No document selected'}
          </Typography>
          {selectedDoc && (
            <Chip
              size="small"
              sx={{ bgcolor: 'white', border: `1px solid ${ACCENT_BORDER}` }}
              label={dayjs(selectedDoc.timestamp).format('DD/MM/YYYY HH:mm')}
            />
          )}
        </Box>

        <Box sx={{ p: 3, flex: 1, overflowY: 'auto', bgcolor: '#F8FAFF' }}>
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={18} />
              <Skeleton variant="rounded" height={18} />
              <Skeleton variant="rounded" height={18} />
            </Stack>
          ) : selectedDoc ? (
            <div
              // You control content generation; assuming it's trusted HTML
              dangerouslySetInnerHTML={{ __html: selectedDoc.content }}
              style={{
                lineHeight: 1.6,
                fontSize: '0.95rem',
              }}
            />
          ) : (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              Select a document from the left to view its contents.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
